import React, { createContext, useCallback, useState } from 'react'
import { apiUrls } from '@http/api-urls'
import { httpService } from '@http/service'
import { useGlobalCtx } from '@providers/global-context'
import { SignFormLayout } from '@shared/components/forms/sign/components/layout'
import { Step1 } from '@shared/components/forms/sign/steps/step1'
import { Step1LkClient } from '@shared/components/forms/sign/steps/step1-lk-client'
import { Step2 } from '@shared/components/forms/sign/steps/step2'
import { openBase64Pdf } from '@shared/pipes/base64'
import { AnimateSharedLayout, motion } from 'framer-motion'

import { SignFormSuccess } from './steps/success'
import { getConsentPreview, receiveConsentTypes } from './api'
import { ConsentType, ConsentTypePreview } from './model'

export interface ContextValue {
	currentStep: number
	totalSteps: number
	setOptionalDocsIds: React.Dispatch<React.SetStateAction<Record<string, boolean>>>
	optionalDocsIds: Record<string, boolean>
	isSpoilerOpened: boolean
	toggleSpoiler: () => void
	consentTypes: {
		ids: string[]
		entities: Record<string, ConsentType>
	}

	getConsentTypePreview(type: string): void
	prevStep(): void
	nextStep(): void
}

const Context = createContext<ContextValue>(undefined!)

const steps = {
	1: Step1,
	2: Step2,
	3: SignFormSuccess,
}

const LkSteps = {
	1: Step1LkClient,
	2: Step2,
	3: SignFormSuccess,
}

const errors = {
	RECEIVE_CONSENT: 'Ошибка загрузки согласий для подписания',
	USER: 'Пользователь не найден',
} as const

interface SignFormProps {
	isLkClient?: boolean
}

export const SignForm = ({ isLkClient = false }: SignFormProps) => {
	const [activeStep, setActiveStep] = useState(1)
	const [isSpoilerOpened, setIsSpoilerOpened] = React.useState(false)
	const [consentTypes, setConsentTypes] = React.useState<ContextValue['consentTypes']>({
		ids: [],
		entities: {},
	})
	const [consentTypesPreviews, setConsentTypesPreviews] = React.useState<Record<string, string>>({})
	const [error, setError] = React.useState('')
	const [optionalDocsIds, setOptionalDocsIds] = useState<Record<string, boolean>>({})
	const { setGlobalCtx } = useGlobalCtx()

	React.useEffect(() => {
		;(async () => {
			try {
				const response = await httpService.get<{ phone: string }>(apiUrls.getUser())
				setGlobalCtx({ user: { phone: response.phone } })
			} catch {
				setError(errors.USER)
				return
			}

			try {
				const response = (await receiveConsentTypes()) as ConsentType[]

				const entities = response.reduce((acc, curr) => {
					acc[curr.id] = curr
					return acc
				}, {} as ContextValue['consentTypes']['entities'])
				const ids = Object.keys(entities)

				setConsentTypes({ entities, ids })
			} catch {
				setError(errors.RECEIVE_CONSENT)
			}
		})()
	}, [])

	const getConsentTypePreview = useCallback(
		async (documentId: string) => {
			try {
				if (!consentTypesPreviews[documentId]) {
					const response = (await getConsentPreview(documentId)) as ConsentTypePreview

					setConsentTypesPreviews((prev) => ({
						...prev,
						[response.content]: response.content,
					}))

					openBase64Pdf(response.content)
				} else {
					openBase64Pdf(consentTypesPreviews[documentId])
				}
			} catch {
				//
			}
		},
		[consentTypesPreviews]
	)

	const StepComponent = isLkClient ? LkSteps[activeStep] : steps[activeStep]

	const _handleChangeStep = useCallback(
		(newStep: number) => {
			setActiveStep(newStep)
		},
		[activeStep]
	)

	const handleNextStep = useCallback(
		() => _handleChangeStep(activeStep + 1),
		[_handleChangeStep, activeStep]
	)

	const handlePrevStep = useCallback(
		() => _handleChangeStep(activeStep - 1),
		[_handleChangeStep, activeStep]
	)

	const value: ContextValue = {
		consentTypes,
		isSpoilerOpened,
		setOptionalDocsIds,
		optionalDocsIds,
		getConsentTypePreview,
		toggleSpoiler: () => setIsSpoilerOpened((prev) => !prev),
		currentStep: activeStep,
		nextStep: handleNextStep,
		prevStep: handlePrevStep,
		totalSteps: Object.values(steps).length - 1,
	}

	return (
		<Context.Provider value={value}>
			<SignFormLayout>
				<AnimateSharedLayout>
					<motion.div
						key={activeStep}
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.2 }}
					>
						{consentTypes.ids.length ? (
							<StepComponent />
						) : error ? (
							<h5 style={{ textAlign: 'center' }}>{error}</h5>
						) : (
							<h5 style={{ textAlign: 'center' }}>Запускаем процесс...</h5>
						)}
					</motion.div>
				</AnimateSharedLayout>
			</SignFormLayout>
		</Context.Provider>
	)
}

export const useSignForm = () => React.useContext(Context) as ContextValue
